import cloneDeep from 'lodash/cloneDeep';
import findLast from 'lodash/last';
import some from 'lodash/some';
import { CONSTANTS } from './Configuration';
import { v4 as uuidv4 } from 'uuid';
import { getDefaultErrors, getEmptyQuestion, getSelectedControlIdFromAppTemplateControlType, reorderAllControls } from './TemplateHelpers';

export const handleAddSection = (applicationPages, applicationSections, applicationGroups, applicationQuestions, selectedControl) => {
    if (!applicationPages || !applicationSections || !applicationQuestions || !applicationGroups || !selectedControl) {
        return {
            newSections: [],
            newGroups: [],
            newQuestions: [],
            newSelectedControl: {}
        };
    }
    const { appTemplateControlTypes, controlTypes } = CONSTANTS;
    const defaultErrors = getDefaultErrors();
    const newSection = {
        id: uuidv4(),
        title: '',
        pageId: selectedControl.pageId,
        showTitle: true,
        errors: cloneDeep(defaultErrors),
        subheaderTop: 'true',
    };
    const allSections = [...applicationSections, newSection];

    // Add a blank group to the section
    const newGroup = {
        id: uuidv4(),
        sectionId: newSection.id,
        pageId: selectedControl.pageId,
    };
    const allGroups = [...applicationGroups, newGroup];

    // Add an empty question to the group
    const newQuestion = {
        id: uuidv4(),
        label: '',
        pageId: selectedControl.pageId,
        sectionId: newSection.id,
        groupId: newGroup.id,
        type: controlTypes.empty,
        errors: cloneDeep(defaultErrors),
    };
    const allQuestions = [...applicationQuestions, newQuestion];

    const { newSections, newGroups, newQuestions } = reorderAllControls(applicationPages, allSections, allGroups, allQuestions);
    const newSelectedControl= {
        ...selectedControl,
        controlType: appTemplateControlTypes.section,
        sectionId: newSection.id,
        groupId: null,
        questionId: null,
    };
    return { newSections, newGroups, newQuestions, newSelectedControl };
};

export const handleAddTemplateSection = (applicationGroups, applicationQuestions, selectedControl, selectedEmptyQuestion, newQuestionsArray, questionIndex) => {
    const allQuestions = applicationQuestions;
    const allGroups = applicationGroups;
    const { appTemplateControlTypes, controlTypes, maxQuestionsPerRow } = CONSTANTS;
    const defaultErrors = getDefaultErrors();
    const firstGroupIndex = allGroups.findIndex(x => x.id === selectedEmptyQuestion.groupId);
    let newGroupId = selectedControl.groupId;
    let currentGroupLength = allQuestions.filter(q => q.type !== controlTypes.empty && q.groupId === newGroupId).length;
    // If the user clicked on an empty that was the only question in the group,
    // we want to add a new group with an empty.
    const groupsToAdd = [];
    const questionsToAdd = [];

    newQuestionsArray.forEach(question => {
        if (++currentGroupLength > maxQuestionsPerRow) {
            currentGroupLength = 1;
            newGroupId = uuidv4();

            groupsToAdd.push({
                id: newGroupId,
                pageId: selectedEmptyQuestion.pageId,
                sectionId: selectedEmptyQuestion.sectionId
            });
        }

        questionsToAdd.push({
            ...question,
            pageId: selectedEmptyQuestion.pageId,
            sectionId: selectedEmptyQuestion.sectionId,
            groupId: newGroupId,
            errors: cloneDeep(defaultErrors)
        });
    });

    if (some(questionsToAdd)) {
        allQuestions.splice(questionIndex, 1, ...questionsToAdd);
    }

    if (some(groupsToAdd)) {
        allGroups.splice(firstGroupIndex + 1, 0, ...groupsToAdd);
    }

    const newSelectedQuestion = findLast(questionsToAdd, q => q.type !== controlTypes.empty);
    let newSelectedControl = {
        ...selectedControl,
        controlType: appTemplateControlTypes.question,
        groupId: newSelectedQuestion.groupId,
        questionId: newSelectedQuestion.id
    };

    return { allGroups, allQuestions, newSelectedControl };
};

export const handleAddingEmptyQuestions = (questions, groups, sections, pages, currentQuestion) => {
    // If there are NOT 3 questions, add empty question to the next index of group
    //      If group is last - there are NO more groups below (groupIndex >= section.length - 1)
    //              Create 1 new group on the next index and create 1 empty question in that group
    //      If group is not last - there are groups below (groupIndex < section.length - 1)
    //               Do nothing
    // If there are already 3 questions
    //      If group is last - there are NO more groups below (groupIndex >= section.length - 1)
    //              Create 1 new group on the next index and create 1 empty question in that group
    //      If group is not last - there are groups below (groupIndex < section.length - 1)
    //               Do nothing
    const { controlTypes, columnWidthOptions, maxQuestionsPerRow } = CONSTANTS;
    let allQuestions = questions;
    let allGroups = groups;
    let questionsInGroup = questions.filter(q => q.groupId === currentQuestion.groupId && q.type !== controlTypes.empty);
    let currentQuestionIndex = questions.findIndex(q => q.id === currentQuestion.questionId);
    const currentGroups = groups.filter(g => g.sectionId === currentQuestion.sectionId);
    let currentGroupIndex = currentGroups.findIndex(g => g.id === currentQuestion.groupId);
    const isGroupFull = questionsInGroup.length >= maxQuestionsPerRow || (questionsInGroup.length == 1 && questionsInGroup[0].columnWidth == columnWidthOptions.extended);
    const questionsToAdd = [];
    const groupsToAdd = [];

    if (!isGroupFull) {
        questionsToAdd.push(
            getEmptyQuestion(currentQuestion.pageId, currentQuestion.sectionId, currentQuestion.groupId)
        );
    }

    if (currentGroupIndex === currentGroups.length - 1) {
        const newGroupId = uuidv4();
        const newGroup = {
            id: newGroupId,
            sectionId: currentQuestion.sectionId,
            pageId: currentQuestion.pageId,
        };
        groupsToAdd.push(newGroup);
        questionsToAdd.push(
            getEmptyQuestion(currentQuestion.pageId, currentQuestion.sectionId, newGroupId)
        );
    }

    if (some(questionsToAdd)) {
        allQuestions.splice(currentQuestionIndex + 1, 0, ...questionsToAdd);
    }

    if (some(groupsToAdd)) {
        allGroups.splice(currentGroupIndex + 1, 0, ...groupsToAdd);
    }

    return { allQuestions, allGroups };
};

export const handleRemoveControl = (applicationPages, applicationSections, applicationGroups, applicationQuestions, selectedControl) => {
    if (!applicationPages || !applicationSections || !applicationQuestions || !applicationGroups || !selectedControl) {
        return {
            newPages: [],
            newSections: [],
            newGroups: [],
            newQuestions: [],
            newSelectedControl: {}
        };
    }
    const id = getSelectedControlIdFromAppTemplateControlType(selectedControl);
    const controlType = selectedControl?.controlType ?? null;
    if (!id || !controlType) {
        return {
            newPages: [],
            newSections: [],
            newGroups: [],
            newQuestions: [],
            newSelectedControl: {}
        };
    }

    let newSelectedControl =selectedControl ;
    let pagesToRemove = [], sectionsToRemove = [], groupsToRemove = [], questionsToRemove = [];
    let newPages = applicationPages;
    let newSections = applicationSections;
    let newGroups = applicationGroups;
    let newQuestions = applicationQuestions;
    const { appTemplateControlTypes, controlTypes, maxQuestionsPerRow } = CONSTANTS;
    const defaultErrors = getDefaultErrors();
    switch (controlType) {
        case appTemplateControlTypes.page: {
            const pageIndex = applicationPages.findIndex(p => p.id === id);
            pagesToRemove.push(pageIndex);
            if (selectedControl.pageId === id) {
                if (applicationPages.length <= 1) {
                    newSelectedControl = { ...newSelectedControl, pageId: null };
                }
                // if first page, select next page else select previous page
                else if (applicationPages[0].id === id) {
                    newSelectedControl = { ...newSelectedControl, pageId: applicationPages[1].id };
                }
                else {
                    newSelectedControl = { ...newSelectedControl, pageId: applicationPages[pageIndex - 1].id };
                }
            }
            // Get the indexes for downstream controls to be removed
            newSections.forEach((x, i) => x.pageId === id && sectionsToRemove.push(i));
            newGroups.forEach((x, i) => x.pageId === id && groupsToRemove.push(i));
            newQuestions.forEach((x, i) => x.pageId === id && questionsToRemove.push(i));
            break;
        }

        case appTemplateControlTypes.section: {
            sectionsToRemove.push(applicationSections.findIndex(s => s.id === id));
            if (selectedControl.sectionId === id) {
                const pageSections = applicationSections.filter(s => s.pageId === selectedControl.pageId);
                const pageSectionIndex = pageSections.findIndex(s => s.id === id);
                if (pageSections.length <= 1) {
                    newSelectedControl = { ...newSelectedControl, controlType: appTemplateControlTypes.page, sectionId: null };
                }
                // if first section, select next section else select previous section
                else if (pageSections[0].id === id) {
                    newSelectedControl = { ...newSelectedControl, sectionId: pageSections[1].id };
                }
                else {
                    newSelectedControl = { ...newSelectedControl, sectionId: pageSections[pageSectionIndex - 1].id };
                }
            }
            // Get the indexes for downstream controls to be removed
            newGroups.forEach((x, i) => x.sectionId === id && groupsToRemove.push(i));
            newQuestions.forEach((x, i) => x.sectionId === id && questionsToRemove.push(i));
            break;
        }

        case appTemplateControlTypes.question: {
            questionsToRemove.push(applicationQuestions.findIndex(q => q.id === id));
            const question = applicationQuestions.find(q => q.id === id);
            const groupQuestions = applicationQuestions.filter(q => q.groupId === question.groupId);
            if (selectedControl.questionId === id) {
                newSelectedControl = {
                    ...newSelectedControl,
                    controlType: appTemplateControlTypes.section,
                    questionId: null,
                    groupId: null,
                };
            }
            // If only question in group, remove group
            if (groupQuestions.filter(x => x.type !== controlTypes.empty).length === 1) {
                groupsToRemove.push(applicationGroups.findIndex(g => g.id === question.groupId));
            }
            // If max questions in group, add empty question
            else if (groupQuestions.filter(x => x.type !== controlTypes.empty).length === maxQuestionsPerRow) {
                const lastQuestionInGroup = groupQuestions.slice(-1)[0];
                const lastQuestionInGroupIndex = newQuestions.findIndex(q => q.id === lastQuestionInGroup.id);
                newQuestions.splice(lastQuestionInGroupIndex + 1, 0, {
                    id: uuidv4(),
                    pageId: question.pageId,
                    sectionId: question.sectionId,
                    groupId: question.groupId,
                    label: '',
                    type: controlTypes.empty,
                    errors: cloneDeep(defaultErrors),
                });
            }
            break;
        }
        default:
            break;
    }

    for (let i = questionsToRemove.length - 1; i >= 0; i--) {
        newQuestions.splice(questionsToRemove[i], 1);
    }
    for (let i = groupsToRemove.length - 1; i >= 0; i--) {
        newGroups.splice(groupsToRemove[i], 1);
    }
    for (let i = sectionsToRemove.length - 1; i >= 0; i--) {
        newSections.splice(sectionsToRemove[i], 1);
    }
    for (let i = pagesToRemove.length - 1; i >= 0; i--) {
        newPages.splice(pagesToRemove[i], 1);
    }
    return { newPages, newSections, newGroups, newQuestions, newSelectedControl };
};